import CloseButton from './close-button';
import reactStringReplace from 'react-string-replace';
import {Transition} from '@headlessui/react';
import {isMobile} from '../../utils/helpers';
import {useAppContext} from '../../app-context';
import {useCallback, useEffect, useRef, useState} from 'react';

type Props = {
    callback: (succeeded: boolean) => void,
    expertiseAutofocus?: boolean
};

const initialsOf = (name: string) => (name.trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('') || '?').toUpperCase();

const AddDetailsPopup = (props: Props) => {
    const inputNameRef = useRef<HTMLInputElement>(null);
    const inputExpertiseRef = useRef<HTMLInputElement>(null);
    const {dispatchAction, member, colorScheme, t} = useAppContext();

    const resolveDark = useCallback(() => {
        if (colorScheme === 'dark') {
            return true;
        }
        if (colorScheme === 'light') {
            return false;
        }
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }, [colorScheme]);

    const [isDark, setIsDark] = useState(resolveDark);

    useEffect(() => {
        setIsDark(resolveDark());
        if (colorScheme === 'dark' || colorScheme === 'light') {
            return;
        }
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const update = () => setIsDark(mq.matches);
        mq.addEventListener('change', update);
        return () => mq.removeEventListener('change', update);
    }, [colorScheme, resolveDark]);

    const [name, setName] = useState(member.name ?? '');
    const [expertise, setExpertise] = useState(member.expertise ?? '');

    const maxExpertiseChars = 50;
    let initialExpertiseChars = maxExpertiseChars;
    if (member.expertise) {
        initialExpertiseChars -= member.expertise.length;
    }
    const [expertiseCharsLeft, setExpertiseCharsLeft] = useState(initialExpertiseChars);

    const [error, setError] = useState({name: '', expertise: ''});

    const stopPropagation = (event: Event) => {
        event.stopPropagation();
    };

    const close = (succeeded: boolean) => {
        dispatchAction('closePopup', {});
        props.callback(succeeded);
    };

    const submit = async () => {
        if (name.trim() !== '') {
            await dispatchAction('updateMember', {
                name,
                expertise
            });
            close(true);
        } else {
            setError({name: t('Enter your name'), expertise: ''});
            setName('');
            inputNameRef.current?.focus();
        }
    };

    useEffect(() => {
        if (!isMobile()) {
            const timer = setTimeout(() => {
                if (props.expertiseAutofocus) {
                    inputExpertiseRef.current?.focus();
                } else {
                    inputNameRef.current?.focus();
                }
            }, 200);

            return () => {
                clearTimeout(timer);
            };
        }
    }, [inputNameRef, inputExpertiseRef, props.expertiseAutofocus]);

    const exampleProfiles = [
        {name: 'James Fletcher', expertise: t('Frontend engineer')},
        {name: 'Naomi Schiff', expertise: t('Indie hacker')},
        {name: 'Franz Tost', expertise: t('ML researcher')},
        {name: 'Katrina Klosp', expertise: t('DevOps lead')}
    ];

    const avatarColors = ['var(--fg)', 'var(--sub)', 'var(--action)', 'var(--muted)'];

    const renderExampleProfiles = () => {
        return exampleProfiles.map((profile, i) => (
            <Transition
                key={profile.name}
                appear
                enter={`transition duration-200 delay-[400ms] ease-out`}
                enterFrom="opacity-0 translate-y-2"
                enterTo="opacity-100 translate-y-0"
                leave="transition duration-200 ease-in"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-2"
            >
                <div className="flex flex-row items-center justify-start gap-3 pr-4">
                    <div
                        className="flex size-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold"
                        style={{background: avatarColors[i % avatarColors.length], color: 'var(--bg)'}}
                    >
                        {initialsOf(profile.name)}
                    </div>
                    <div className="flex flex-col items-start justify-center">
                        <div className="font-sans text-base font-semibold tracking-tight" style={{color: 'var(--fg)'}}>
                            {profile.name}
                        </div>
                        <div className="font-sans text-sm tracking-tight" style={{color: 'var(--muted)'}}>
                            {profile.expertise}
                        </div>
                    </div>
                </div>
            </Transition>
        ));
    };

    const charsText = reactStringReplace(t('{amount} characters left'), '{amount}', () => {
        return <b>{expertiseCharsLeft}</b>;
    });

    const inputStyle = {
        background: 'transparent',
        borderColor: 'var(--input-border)',
        color: 'var(--fg)'
    };

    return (
        <div
            className={`ghost-display ${isDark ? 'dark ' : ''}rounded-none relative h-screen w-screen overflow-hidden p-[28px] text-center shadow-modal sm:h-auto sm:w-[720px] sm:rounded-xl sm:p-0`}
            data-testid="profile-modal"
            style={{background: 'var(--bg)'}}
            onMouseDown={stopPropagation}
        >
            <div className="flex">
                <div className="hidden w-[50%] flex-col items-center justify-center sm:flex sm:p-8" style={{background: 'var(--faint)', borderRight: '1px solid var(--border)'}}>
                    <div className="mt-[-1px] flex flex-col gap-9 text-left">
                        {renderExampleProfiles()}
                    </div>
                </div>
                <div className={`p-0 sm:p-8`}>
                    <h1 className="mb-1 text-center font-sans text-2xl font-bold tracking-tight sm:text-left" style={{color: 'var(--fg)'}}>{t('Complete your profile')}<span className="hidden sm:inline">.</span></h1>
                    <p className="pr-0 text-center font-sans text-md leading-snug sm:pr-10 sm:text-left" style={{color: 'var(--sub)'}}>{t('Add context to your comment, share your name and expertise to foster a healthy discussion.')}</p>
                    <section className="mt-8 text-left">
                        <div className="mb-2 flex flex-row justify-between">
                            <label className="font-sans text-base font-semibold" htmlFor="comments-name" style={{color: 'var(--fg)'}}>{t('Name')}</label>
                            <Transition
                                enter="transition duration-300 ease-out"
                                enterFrom="opacity-0"
                                enterTo="opacity-100"
                                leave="transition duration-100 ease-out"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                show={!!error.name}
                            >
                                <div className="font-sans text-sm text-red-500">{error.name}</div>
                            </Transition>
                        </div>
                        <input
                            ref={inputNameRef}
                            className={`flex h-[42px] w-full items-center rounded border px-3 font-sans text-[16px] outline-0 transition-[border-color] duration-200 ${error.name && 'border-red-500 focus:border-red-500'}`}
                            data-testid="name-input"
                            id="comments-name"
                            maxLength={64}
                            name="name"
                            placeholder={t('Jamie Larson')}
                            style={inputStyle}
                            type="text"
                            value={name}
                            onChange={(e) => {
                                setName(e.currentTarget.value);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    setName(e.currentTarget.value);
                                    // eslint-disable-next-line no-console
                                    submit().catch(console.error);
                                }
                            }}
                        />
                        <div className="mb-2 mt-6 flex flex-row justify-between">
                            <label className="font-sans text-base font-semibold" htmlFor="comments-name" style={{color: 'var(--fg)'}}>{t('Expertise')}</label>
                            <div className="font-sans text-base" style={{color: expertiseCharsLeft === 0 ? '#ef4444' : 'var(--muted)'}}>{charsText}</div>
                        </div>
                        <input
                            ref={inputExpertiseRef}
                            className={`flex h-[42px] w-full items-center rounded border px-3 font-sans text-[16px] outline-0 transition-[border-color] duration-200 ${(expertiseCharsLeft === 0) && 'border-red-500 focus:border-red-500'}`}
                            data-testid="expertise-input"
                            id="comments-expertise"
                            maxLength={maxExpertiseChars}
                            name="expertise"
                            placeholder={t('Head of Marketing at Acme, Inc')}
                            style={inputStyle}
                            type="text"
                            value={expertise}
                            onChange={(e) => {
                                const expertiseText = e.currentTarget.value;
                                setExpertiseCharsLeft(maxExpertiseChars - expertiseText.length);
                                setExpertise(expertiseText);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    setExpertise(e.currentTarget.value);
                                    // eslint-disable-next-line no-console
                                    submit().catch(console.error);
                                }
                            }}
                        />
                        <button
                            className={`mt-10 flex h-[42px] w-full items-center justify-center rounded-md px-8 font-sans text-md font-semibold opacity-100 transition-opacity duration-200 ease-linear hover:opacity-90`}
                            data-testid="save-button"
                            style={{background: 'var(--fg)', color: 'var(--bg)'}}
                            type="button"
                            onClick={() => {
                                // eslint-disable-next-line no-console
                                submit().catch(console.error);
                            }}
                        >
                            {t('Save')}
                        </button>
                    </section>
                </div>
                <CloseButton close={() => close(false)} />
            </div>
        </div>
    );
};

export default AddDetailsPopup;
